import React, {useEffect, useState} from "react";
import {observer} from 'mobx-react';
import Drawer from "@mui/material/Drawer";
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {OnSwitch} from "../../assets/live-svg/OnSwitch";
import {OffSwitch} from "../../assets/live-svg/OffSwitch";
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ConditionalComponent from "./ConditionalComponent";
import Utility from "../../Utility";
import {withStyles} from "@mui/styles";
import {
    notificationActionEnum,
    notificationCategoryEnum,
    notificationTypeEnum,
} from "../../serverClientShared/sharedEnums";
import {routes} from "../../App";
import Button from "@mui/material/Button";
import {useStores} from "../../Hooks";
import {history} from "../../index";

const notificationCategories = Object.keys(notificationCategoryEnum);

const SmallTooltip = withStyles({
    tooltip: {
        height: 16,
        display: "flex",
        alignItems: "center",
    },
    arrow: {
        fontSize: 8,
    },
    tooltipPlacementLeft: {
        left: 15
    }
})(Tooltip);

function sortDates(a, b) {
    return new Date(b).valueOf() - new Date(a).valueOf()
}

function subSortDates(a, b) {
    return new Date(b.CreatedIn).valueOf() - new Date(a.CreatedIn).valueOf()
}

const viewOptions = [
    'showAll',
    'showOnlyUnread',
    'showOnlyRead'
]

const notificationTypeTitles = {
    TASK_ASSIGNED: 'aTaskWasAssignedToMe',
    TASK_STATUS_CHANGED: 'statusOfATaskHasChangedDoneNotDone',
    TASK_DUE_SOON: 'aTaskDeadlineIsDueSoon',
    PROCEDURE_PUBLISHED: 'aNewProcedureVersionHasBeenPublished',
    PROCEDURE_SITE_PREP_PUBLISH_PENDING: 'sitePrepItemPublicationApprovalPending',
    DOMAIN_NEW_USER: 'aNewUserHasJoinedTheDomain',
    DOMAIN_MAX_USERS_SOON: 'workspaceCloseToMaxUsers',
    DOMAIN_PENDING_PAY_PER_CLICK: 'newPayPerClickSharingRequest',
    DOMAIN_PAY_PER_CLICK_LIMIT_SOON: 'payPerClickSharingOptionCloseToMaxSessions',
}

export const NotificationsPanel = observer(() => {
    const {generalStore, dataStore, modalStore, procedureStore} = useStores();

    const [deleteDate, setDeleteDate] = useState(null);
    const [deleteOption, setDeleteOption] = useState(null);
    const [isolateClick, setIsolateClick] = useState(false);
    const [acceptDeclineDisabled, setAcceptDeclineDisabled] = useState(false);
    const anchorRef = React.useRef(null);
    const notificationSettings = !!generalStore.userDetails && !!generalStore.userDetails.NotificationSettings ? generalStore.userDetails.NotificationSettings : {};

    const isWorkspaceAdmin = generalStore.checkWorkspaceAdmin;

    function renderDate(date) {
        if (new Date().toDateString() === date) {
            return generalStore.translate('today')
        } else if (new Date(new Date().valueOf() - 1000 * 60 * 60 * 24).toDateString() === date) {
            return generalStore.translate('yesterday')
        } else return new Date(date).toLocaleDateString()
    }

    function useOutsideAlerter(ref) {
        useEffect(() => {
            function handleClickOutside(event) {
                if (ref.current && !ref.current.contains(event.target)) {
                    if (generalStore.notificationExternalOpen) {
                        generalStore.notificationExternalOpen = false
                    } else {
                        generalStore.closeNotificationPanel()
                    }
                }
            }

            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }, [ref]);
    }

    useOutsideAlerter(anchorRef);

    async function openPayPerClick(payPerClickId) {
        generalStore.isLoaderOpen = true;
        console.log(payPerClickId)
        const payPerClick = await procedureStore.getPayPerClick(payPerClickId);
        const toUpdate = {...payPerClick}
        toUpdate.IsPending = false;
        toUpdate.IsActive = true;
        toUpdate.CreationDate = new Date();
        toUpdate.ApprovedBy = generalStore.userDetails._id;
        modalStore.modalData = {
            payPerClick: toUpdate,
            afterSave: async (updatedPayPerClick) => {
                generalStore.payPerClickSharing.loadingPending = true;
                generalStore.payPerClickSharing.loadingApproved = true;
                await generalStore.getAllPayPerClicks(true);
                generalStore.addPayPerClickToApprovedList(updatedPayPerClick);
                generalStore.payPerClickSharing.loadingPending = false;
                generalStore.payPerClickSharing.loadingApproved = false;
            }
        };
        navigateAway(routes.WORKSPACE_PAY_PER_CLICK);
        modalStore.isPayPerClickSharingOpen = true;
        generalStore.isLoaderOpen = false;
    }

    async function onAccept(notification) {
        setAcceptDeclineDisabled(true);
        switch (notification.Type) {
            case notificationTypeEnum.DOMAIN_PENDING_PAY_PER_CLICK:
                await openPayPerClick(notification.ConnectedTo);
                break;

            case notificationTypeEnum.PROCEDURE_SITE_PREP_PUBLISH_PENDING:
                await procedureStore.setCurrentProcedure(notification.ConnectedTo, false, generalStore.workspace);
                modalStore.modalData = {
                    directFromNotification: true,
                    currentVersionStr: procedureStore.currentProcedure.CurrentVersion,
                };
                modalStore.isPublishVersionModal = true;
                return
            default:
        }

        notification.Action = null;
        if (notification.DescriptionKeys && notification.DescriptionKeys.length > 0) {
            notification.DescriptionKeys.push("$$-", "accepted")
        } else notification.Description += ` - ${generalStore.translate('accepted')}`;
        notification.IsUnread = false;
        await generalStore.updateNotification(notification);
        setAcceptDeclineDisabled(false);
    }

    async function onDecline(notification) {
        setAcceptDeclineDisabled(true);
        switch (notification.Type) {
            case notificationTypeEnum.DOMAIN_PENDING_PAY_PER_CLICK:
                await procedureStore.deletePendingPayPerClick(notification.ConnectedTo);
                break;
            default:
        }

        notification.Action = null;
        if (notification.DescriptionKeys && notification.DescriptionKeys.length > 0) {
            notification.DescriptionKeys.push("$$-", "declined")
        } else notification.Description += ` - ${generalStore.translate('declined')}`;
        notification.IsUnread = false;
        await generalStore.updateNotification(notification);
        setAcceptDeclineDisabled(false);

    }

    const renderNotifications = () => {
        const sortedNotifications = generalStore.sortedNotifications;
        const dates = [...sortedNotifications.keys()].sort(sortDates);

        return (
            <div className="notifications-container">
                {dates.map((date, i) => {
                        const dateNotifications = [...sortedNotifications.get(date)].sort(subSortDates)
                        return (
                            <div className="notification-date-container" key={i}>
                                <div
                                    className="notification-date-header"
                                    onMouseEnter={() => setDeleteDate(date)}
                                    onMouseLeave={() => setDeleteDate(null)}
                                >
                                    <div/>
                                    <div className="notification-date-title">{renderDate(date)}</div>
                                    <div className="delete-notification-icon-container">
                                        <ConditionalComponent if={deleteDate === date}>
                                            <SmallTooltip title={<span
                                                className="notification-tooltip">{generalStore.translate('deleteFromSentence1_beforeDate')} {renderDate(date)} {generalStore.translate('deleteFromSentence2_afterDate')}</span>}
                                                          placement="left" arrow>
                                                <DeleteOutlineIcon
                                                    onClick={() => deleteMany(dateNotifications, renderDate(date))}
                                                    fontSize="inherit"/>
                                            </SmallTooltip>
                                        </ConditionalComponent>
                                    </div>
                                </div>
                                {dateNotifications.map((notification, j) => {
                                        const showAcceptDecline = notification.Action === notificationActionEnum.ACCEPT_DECLINE && notification.IsUnread;
                                        return (
                                            <div key={`${i}_${j}`} className="notification-container">
                                                <SmallTooltip title={<span
                                                    className="notification-tooltip">{generalStore.translate(notification.IsUnread ? "markRead" : "markUnread")}</span>}
                                                              arrow placement="left">
                                                    <div
                                                        className={`notification-unread-marker${notification.IsUnread ? " unread" : ""}`}
                                                        onClick={() => toggleUnread(notification, !notification.IsUnread)}
                                                    />
                                                </SmallTooltip>
                                                <Card
                                                    className={`notification-card${notification.IsUnread ? " unread" : ""}`}
                                                    onMouseEnter={() => setDeleteOption(notification._id)}
                                                    onMouseLeave={() => setDeleteOption(null)}
                                                    onClick={() => activateNotification(notification)}
                                                >
                                                    <CardContent>
                                                        <div className="notification-header">
                                                            {generalStore.getNotificationHeader(notification)}
                                                        </div>
                                                        <div
                                                            dangerouslySetInnerHTML={{__html: `<span>${generalStore.getNotificationDescription(notification)}</span>`}}
                                                            className="notification-body"
                                                        />
                                                        <ConditionalComponent if={showAcceptDecline}>
                                                            <div className='notification-buttons'>
                                                                <Button
                                                                    onClick={() => onAccept(notification)}
                                                                    onMouseEnter={() => setIsolateClick(true)}
                                                                    onMouseLeave={() => setIsolateClick(false)}
                                                                    disabled={acceptDeclineDisabled}
                                                                    color='primary'
                                                                    variant='outlined'
                                                                >
                                                                    {generalStore.translate('accept')}
                                                                </Button>
                                                                <Button
                                                                    onClick={() => onDecline(notification)}
                                                                    onMouseEnter={() => setIsolateClick(true)}
                                                                    onMouseLeave={() => setIsolateClick(false)}
                                                                    disabled={acceptDeclineDisabled}
                                                                    variant='outlined'
                                                                    className='delete'
                                                                >
                                                                    {generalStore.translate('decline')}
                                                                </Button>
                                                            </div>
                                                        </ConditionalComponent>

                                                        <div className="notification-card-bottom">
                                                            <div className="notification-time">
                                                                {generalStore.translate('atSentence1_beforeDate')} {Utility.renderTime_HHMM(new Date(notification.CreatedIn))} {generalStore.translate('atSentence2_afterDate')}
                                                            </div>
                                                            <div className="delete-notification-icon-container">
                                                                <ConditionalComponent if={deleteOption === notification._id}>
                                                                    <SmallTooltip
                                                                        title={<span
                                                                            className="notification-tooltip">{generalStore.translate('delete')}</span>}
                                                                        placement="left" arrow>
                                                                        <DeleteOutlineIcon
                                                                            onClick={() => deleteOne(notification._id)}
                                                                            onMouseEnter={() => setIsolateClick(true)}
                                                                            onMouseLeave={() => setIsolateClick(false)}
                                                                            fontSize="inherit"
                                                                        />
                                                                    </SmallTooltip>
                                                                </ConditionalComponent>
                                                            </div>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            </div>
                                        )
                                    }
                                )}
                            </div>
                        )
                    }
                )}
            </div>
        )
    };

    const renderHeader = () => {
        const pushActive = checkPushActive()

        return (
            <div className="notifications-header">
                <Button className='notifications-settings-button' onClick={() => toggleOpenSettings()}>
                    {generalStore.notificationsSettingsOpen
                        ? <ArrowBackIcon className="arrow-back"/>
                        : <SettingsOutlinedIcon/>
                    }
                </Button>
                <div
                    className="notifications-heading">{generalStore.translate(generalStore.notificationsSettingsOpen ? "pushNotificationSettings" : "notifications")}</div>
                <div
                    onClick={() => generalStore.notificationsSettingsOpen ? togglePushSettingsOn() : deleteAll()}
                    className={generalStore.notificationsSettingsOpen ? "switch-icon" : "delete-icon"}
                >
                    {generalStore.notificationsSettingsOpen
                        ? pushActive
                            ? <OnSwitch viewBox="0 0 41 20"/>
                            : <OffSwitch viewBox="0 0 41 20"/>
                        :
                        <SmallTooltip
                            title={<span className="notification-tooltip">{generalStore.translate("deleteAll")}</span>}
                            placement="left"
                            arrow>
                            <DeleteOutlineIcon/>
                        </SmallTooltip>
                    }
                </div>
            </div>
        )
    }

    const renderOptions = () => {
        const unreadSettings = !!notificationSettings.ShowUnread ? notificationSettings.ShowUnread : 1;

        return (
            <div className="notifications-header">
                <TextField
                    className="choose-view-unread-menu"
                    select
                    value={unreadSettings}
                    onChange={handleChangeUnreadSettings}
                    variant="outlined"
                    SelectProps={{
                        onOpen: () => generalStore.notificationExternalOpen = true,
                    }}
                >
                    {viewOptions.map((option, i) => (
                        <MenuItem key={i} value={i + 1} className="view-unread-menu-item">
                            {generalStore.translate(option)}
                        </MenuItem>
                    ))}
                </TextField>
                <div
                    className="mark-notifications-as-read"
                    onClick={markAllAsRead}
                >
                    {generalStore.translate(generalStore.numUnreadNotifications === 0
                        ? "markAllAsUnread"
                        : "markAllAsRead"
                    )}
                </div>
            </div>
        )
    }

    const toggleOpenSettings = () => generalStore.notificationsSettingsOpen = !generalStore.notificationsSettingsOpen;

    const checkPushActive = () => {
        let active = false
        for (let categoryEnum of notificationCategories) {
            if (categoryEnum === "DOMAIN" && !isWorkspaceAdmin) {
                continue
            }
            if (checkAnyActive(categoryEnum)) {
                active = true;
                break
            }
        }
        return active
    }

    const togglePushSettingsOn = () => {
        const active = !checkPushActive()
        let category = notificationSettings.Category;
        if (!category) {
            category = {}
        }
        notificationCategories.forEach(categoryEnum => {
            if (!category[categoryEnum]) {
                category[categoryEnum] = {}
            }
            for (let typeKey of Object.keys(category[categoryEnum])) {
                category[categoryEnum][typeKey] = active
            }
        })
        generalStore.changeNotificationSettings('Category', category);
        if (active) {
            Utility.checkPushNotificationPermission()
        }
    }

    const handleChangeUnreadSettings = event => generalStore.changeNotificationSettings('ShowUnread', event.target.value);

    const markAllAsRead = async () => await generalStore.markAllNotificationsRead();

    const toggleUnread = (notification, isUnread) => generalStore.toggleUnread(notification._id, isUnread);

    const deleteAll = () => {
        modalStore.modalData = {
            title: generalStore.translate('deleteAllNotifications'),
            message: generalStore.translate('areYouSureYouWishToDeleteAllNotifications'),
            action: generalStore.deleteAllNotifications,
        }
        modalStore.isDeleteNotificationsModalOpen = true;
        generalStore.notificationExternalOpen = true;
    }

    const deleteMany = (notifications, date) => {
        modalStore.modalData = {
            title: `${generalStore.translate('deleteNotificationsFromSentence1_beforeDate')} ${date}${generalStore.translate('deleteNotificationsFromSentence2_afterDate')}`,
            message: `${generalStore.translate('deleteNotificationsConfirmSentence1_beforeDate')} ${String(date).toLowerCase()}${generalStore.translate('deleteNotificationsConfirmSentence2_afterDate')}`,
            action: generalStore.deleteManyNotifications,
            arg: notifications
        }
        modalStore.isDeleteNotificationsModalOpen = true;
        generalStore.notificationExternalOpen = true;
    }

    const deleteOne = notificationId => generalStore.deleteNotification(notificationId);

    const activateNotification = notification => {
        if (!isolateClick) {
            doNotificationAction(notification)
            toggleUnread(notification, false)
        }
    }

    const navigateAway = link => {
        generalStore.closeNotificationPanel();
        history.push(link)
    }

    const doNotificationAction = notification => {
        switch (notification.Type) {
            case notificationTypeEnum.PROCEDURE_PUBLISHED:
                const procedureLink = Utility.buildUrl(routes.VIEWER, {
                    db: generalStore.workspace,
                    procedureId: notification.ConnectedTo
                })
                return navigateAway(procedureLink)
            case notificationTypeEnum.NEW_COLLABORATOR:
                const knowledgeBaseLink = Utility.buildUrl(routes.PROJECT_PROCEDURES, {projectId: notification.ConnectedTo})
                return navigateAway(knowledgeBaseLink)
            case notificationTypeEnum.DOMAIN_NEW_USER:
                return navigateAway(routes.MEMBERS)
            case notificationTypeEnum.DOMAIN_PENDING_PAY_PER_CLICK:
                return navigateAway(routes.WORKSPACE_PAY_PER_CLICK)
            case notificationTypeEnum.DOMAIN_PAY_PER_CLICK_LIMIT_SOON:
                return openPayPerClick(notification.ConnectedTo);
            case notificationTypeEnum.DOMAIN_MAX_USERS_SOON:
                return navigateAway(routes.MEMBERS)
            default:
                return
        }
    }

    const renderNotificationSettings = () => {
        const active = checkPushActive()
        return (
            <div className={`notification-settings-container${!active ? " disabled" : ""}`}>
                <div className="push-notifications-options">
                    {notificationCategories.map((category, i) => renderNotificationOption(category, i))}
                </div>
                <div className="notifications-settings-media-options-container">
                    {renderEmailSettings()}
                </div>
                <div className="notifications-settings-info">
                    {generalStore.translate("pushNotificationSettingsApplyToAllWorkspaces")}
                </div>
            </div>
        )
    }

    const getCategoryName = categoryEnum => {
        switch (categoryEnum) {
            case "TASK":
                return "tasks"
            case "PROCEDURE":
                return "knowledgeBase"
            case "DOMAIN":
                return "workspaceManagement"
            default:
                return ""
        }
    }

    const checkAnyActive = (categoryEnum) => {
        if (!notificationSettings.Category || !notificationSettings.Category[categoryEnum]) {
            return false
        }
        for (let typeKey of Object.keys(notificationSettings.Category[categoryEnum])) {
            if (notificationSettings.Category[categoryEnum][typeKey]) {
                return true
            }
        }
        return false
    }

    const toggleAllSubSettings = categoryEnum => {
        let category = notificationSettings.Category;
        if (!category) {
            category = {}
        }
        if (!category[categoryEnum]) {
            category[categoryEnum] = {}
        }
        const active = checkAnyActive(categoryEnum);
        for (let typeKey of Object.keys(category[categoryEnum])) {
            category[categoryEnum][typeKey] = !active
        }
        generalStore.changeNotificationSettings("Category", category);
    }

    const toggleSubSetting = (type, categoryEnum) => {
        const category = notificationSettings.Category;
        if (!category || !category[categoryEnum]) {
            category[categoryEnum] = {}
        }
        category[categoryEnum][type] = !category[categoryEnum][type]
        generalStore.changeNotificationSettings("Category", category);
    }


    const renderNotificationOption = (categoryEnum, index) => {

        if (categoryEnum === "DOMAIN" && !isWorkspaceAdmin) {
            return
        }

        const allSubSettingsOn = checkAnyActive(categoryEnum);
        const notificationTypes = Object.keys(notificationTypeEnum);
        const categoryTypes = notificationTypes.filter(t => t.split("_")[0] === categoryEnum);

        const toggleOpenSubSettings = () => {
            if (isolateClick) {
                toggleAllSubSettings(categoryEnum)
            } else {
                if (generalStore.notificationsSettingsCategoryIndex === index + 1) {
                    generalStore.notificationsSettingsCategoryIndex = 0
                } else generalStore.notificationsSettingsCategoryIndex = index + 1
            }
        }

        return (
            <div className={`notification-setting-container${allSubSettingsOn ? "" : " disabled"}`} key={index}>
                <div
                    className={`notification-setting-title-bar${index + 1 === generalStore.notificationsSettingsCategoryIndex ? " opened" : ""}`}
                    onClick={() => toggleOpenSubSettings()}
                >
                    <div className="notification-setting-title">
                        {generalStore.translate(getCategoryName(categoryEnum))}
                    </div>
                    <div
                        className={`notification-setting-display-active${index + 1 === generalStore.notificationsSettingsCategoryIndex ? " opened" : ""}`}>
                        <ConditionalComponent if={index + 1 !== generalStore.notificationsSettingsCategoryIndex}>
                            <div className="notification-setting-on-off">
                                {generalStore.translate(allSubSettingsOn
                                    ? "on"
                                    : "off"
                                ).toUpperCase()}
                            </div>
                        </ConditionalComponent>
                        <ConditionalComponent if={index + 1 === generalStore.notificationsSettingsCategoryIndex}>
                            <div
                                className={`notification-setting-set-on-off switch-icon`}
                                onMouseEnter={() => setIsolateClick(true)}
                                onMouseLeave={() => setIsolateClick(false)}
                            >
                                {allSubSettingsOn
                                    ? <OnSwitch viewBox="0 0 41 20"/>
                                    : <OffSwitch viewBox="0 0 41 20"/>
                                }
                            </div>
                        </ConditionalComponent>
                    </div>
                    <div className="notification-setting-dropdown-arrow">
                        {index + 1 === generalStore.notificationsSettingsCategoryIndex
                            ? <ExpandLessIcon/>
                            : <ExpandMoreIcon/>
                        }
                    </div>
                </div>
                <ConditionalComponent if={index + 1 === generalStore.notificationsSettingsCategoryIndex}>
                    <div className="notification-setting-types">
                        {categoryTypes.map(type => {
                                const subSetting = notificationSettings.Category && notificationSettings.Category[categoryEnum][type]
                                return (
                                    <div className={`notification-setting-type${subSetting ? "" : " disabled"}`} key={type}>
                                        <div>{generalStore.translate(notificationTypeTitles[type])}</div>
                                        <div
                                            className="switch-icon"
                                            onClick={() => toggleSubSetting(type, categoryEnum)}
                                        >
                                            {subSetting
                                                ? <OnSwitch viewBox="0 0 41 20"/>
                                                : <OffSwitch viewBox="0 0 41 20"/>
                                            }
                                        </div>
                                    </div>
                                )
                            }
                        )}
                    </div>
                </ConditionalComponent>
            </div>
        )
    }

    const renderEmailSettings = () => {
        const handleEmailSettingsChange = setting => generalStore.changeNotificationSettings('Emails', setting);

        return (
            <div className="notifications-settings-emails">
                <div className="notifications-settings-options-title">
                    {generalStore.translate('emails')}
                </div>
                <div className="notifications-settings-media-option"
                     onClick={() => notificationSettings.Active && handleEmailSettingsChange(0)}>
                    <div>
                        <div
                            className="notifications-settings-media-option-subtitle">{generalStore.translate('never')}</div>
                        <div
                            className="notifications-settings-media-option-description">{generalStore.translate('doNotSendEmails')}</div>
                    </div>
                    <Radio
                        color="default"
                        checked={Number(notificationSettings.Emails) === 0}
                        inputProps={{'aria-label': generalStore.translate('never')}}
                        disabled={!notificationSettings.Active}
                    />
                </div>
                <div className="notifications-settings-media-option"
                     onClick={() => notificationSettings.Active && handleEmailSettingsChange(1)}>
                    <div>
                        <div
                            className="notifications-settings-media-option-subtitle">{generalStore.translate('daily')}</div>
                        <div
                            className="notifications-settings-media-option-description">{generalStore.translate('sendEmailOnlyOnceADay')}</div>
                    </div>
                    <Radio
                        color="default"
                        checked={Number(notificationSettings.Emails) === 1}
                        inputProps={{'aria-label': generalStore.translate('daily')}}
                        disabled={!notificationSettings.Active}
                    />
                </div>
                <div className="notifications-settings-media-option"
                     onClick={() => notificationSettings.Active && handleEmailSettingsChange(2)}>
                    <div>
                        <div
                            className="notifications-settings-media-option-subtitle">{generalStore.translate('instantly')}</div>
                        <div
                            className="notifications-settings-media-option-description">{generalStore.translate('sendEmailsAsSoonAsPossible')}</div>
                    </div>
                    <Radio
                        color="default"
                        checked={Number(notificationSettings.Emails) === 2}
                        inputProps={{'aria-label': generalStore.translate('instantly')}}
                        disabled={!notificationSettings.Active}
                    />
                </div>
            </div>
        )
    }

    return (
        <Drawer
            className="notification-panel"
            anchor="right"
            open={generalStore.notificationsPanelOpen}
            onClose={generalStore.closeNotificationPanel}
            // variant={generalStore.notificationsPanelOpen ? 'permanent' : 'temporary'}
            ref={anchorRef}
        >
            {renderHeader()}
            <ConditionalComponent
                if={!generalStore.notificationsSettingsOpen && dataStore.notifications.size > 0}>
                {renderOptions()}
                {renderNotifications()}
            </ConditionalComponent>
            <ConditionalComponent if={generalStore.notificationsSettingsOpen}>
                {renderNotificationSettings()}
            </ConditionalComponent>
        </Drawer>
    )
})
